import React, { useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    TextField,
    Typography,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { ChannelActions } from "@Store";
import { actions as dialogActions } from "@Store/Reducers/dialog";
import { IChannel } from "@Interfaces";

const MAX_TITLE_LENGTH = 30;
const MAX_DESC_LENGTH = 100;

type DialogProps = {
    parentChannel?: IChannel;
};

const CreateChannelDialogContent: React.FC<DialogProps> = ({
    parentChannel,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<any>();

    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [cloneParentPolls, setCloneParentPolls] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isSubChannel = !!parentChannel;

    const isInvalid =
        title.length < 3 ||
        title.length > MAX_TITLE_LENGTH ||
        description.length < 3 ||
        description.length > MAX_DESC_LENGTH;

    const handleClose = () => {
        dispatch(dialogActions.hideDialog());
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (isInvalid || isSubmitting) return;

        setIsSubmitting(true);

        try {
            if (isSubChannel && parentChannel) {
                await dispatch(
                    ChannelActions.createSubChannel(parentChannel, {
                        title,
                        description,
                        clone_parent_polls: cloneParentPolls,
                    }) as any
                );
            } else {
                await dispatch(
                    ChannelActions.createChannel({
                        title,
                        description,
                        settings: {
                            display_profile_picture: true,
                        },
                    }) as any
                );
            }
            handleClose();
        } catch (error) {
            console.error(error);
            setIsSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <Typography variant="h6" gutterBottom>
                {isSubChannel
                    ? t("dialogs.sub_channel.create", {
                          defaultValue: "Create Sub-channel",
                      })
                    : t("dialogs.create_event", {
                          defaultValue: "Create Event",
                      })}
            </Typography>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextField
                        value={title}
                        onChange={(event) => setTitle(event.target.value)}
                        label={
                            isSubChannel
                                ? t("dialogs.sub_channel.title_label", {
                                      defaultValue: "Title",
                                  })
                                : t("dialogs.title", {
                                      defaultValue: "Title",
                                  })
                        }
                        variant="outlined"
                        fullWidth
                        required
                        helperText={`${title.length}/${MAX_TITLE_LENGTH}`}
                        autoFocus
                        error={
                            title.length > 0 &&
                            (title.length < 3 ||
                                title.length > MAX_TITLE_LENGTH)
                        }
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        value={description}
                        onChange={(event) => setDescription(event.target.value)}
                        label={
                            isSubChannel
                                ? t("dialogs.sub_channel.description_label", {
                                      defaultValue: "Description",
                                  })
                                : t("dialogs.description", {
                                      defaultValue: "Description",
                                  })
                        }
                        variant="outlined"
                        fullWidth
                        required
                        multiline
                        minRows={3}
                        helperText={`${description.length}/${MAX_DESC_LENGTH}`}
                        error={
                            description.length > 0 &&
                            (description.length < 3 ||
                                description.length > MAX_DESC_LENGTH)
                        }
                    />
                </Grid>
                {isSubChannel && (
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={cloneParentPolls}
                                    onChange={(event) =>
                                        setCloneParentPolls(
                                            event.target.checked
                                        )
                                    }
                                />
                            }
                            label={t(
                                "dialogs.sub_channel.clone_parent_polls_label",
                                {
                                    defaultValue: "Clone parent channel polls",
                                }
                            )}
                        />
                    </Grid>
                )}
            </Grid>
            <Box display="flex" justifyContent="flex-end" marginTop={3}>
                <Button
                    onClick={handleClose}
                    color="secondary"
                    size="small"
                    disabled={isSubmitting}
                >
                    {t("dialogs.cancel")}
                </Button>
                <Button
                    type="submit"
                    color="primary"
                    size="small"
                    variant="contained"
                    disabled={isInvalid || isSubmitting}
                    style={{ marginLeft: 8 }}
                >
                    {t("dialogs.create")}
                </Button>
            </Box>
        </form>
    );
};

export default {
    content: CreateChannelDialogContent,
    showActons: false,
    disableCancelButton: true,
};
